import {omneticApi} from '@dms/api/base';
import {UuidResponseBodySchema} from '@dms/api/shared';

import {
  AddInterestAssignmentConditionApiArg,
  AddInterestAssignmentConditionApiArgSchema,
  AddInterestAssignmentConditionApiResponse,
  AssignAssigneeFromTeamApiArg,
  AssignAssigneeFromTeamApiArgSchema,
  AssigneeTeamListApiArg,
  AssigneeTeamListApiResponse,
  assigneeTeamListApiArgSchema,
  AssignOwnerFromTeamApiArg,
  AssignOwnerFromTeamApiArgSchema,
  ListInterestAssignmentConditionsApiArg,
  listInterestAssignmentConditionsApiArgSchema,
  ListInterestAssignmentConditionsApiResponse,
  OwnerTeamListApiArg,
  OwnerTeamListApiResponse,
  ownerTeamListApiArgSchema,
  RemoveInterestAssignmentConditionApiArg,
  ReorderInterestAssignmentConditionsApiArg,
  ReorderInterestAssignmentConditionsApiArgSchema,
  UpdateInterestAssignmentConditionApiArg,
  UpdateInterestAssignmentConditionApiArgSchema,
} from './types';

export const resourceAssignmentApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    listInterestAssignmentConditions: build.query<
      ListInterestAssignmentConditionsApiResponse,
      ListInterestAssignmentConditionsApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/interest-resource/assignment-condition',
        params: {
          teamId: queryArg.teamId ?? undefined,
        },
      }),
      providesTags: ['InterestAssignmentCondition'],
      extraOptions: {
        requestSchema: listInterestAssignmentConditionsApiArgSchema,
      },
    }),
    addInterestAssignmentCondition: build.mutation<
      AddInterestAssignmentConditionApiResponse,
      AddInterestAssignmentConditionApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/interest-resource/assignment-condition',
        method: 'POST',
        body: queryArg.addInterestAssignmentConditionRequestBody,
      }),
      invalidatesTags: ['InterestAssignmentCondition'],
      extraOptions: {
        requestSchema: AddInterestAssignmentConditionApiArgSchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),
    removeInterestAssignmentCondition: build.mutation<
      void,
      RemoveInterestAssignmentConditionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest-resource/assignment-condition/${queryArg.conditionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['InterestAssignmentCondition'],
    }),
    updateInterestAssignmentCondition: build.mutation<
      void,
      UpdateInterestAssignmentConditionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/interest-resource/assignment-condition/${queryArg.conditionId}`,
        method: 'PUT',
        body: queryArg.updateInterestAssignmentConditionRequestBody,
      }),
      invalidatesTags: ['InterestAssignmentCondition'],
      extraOptions: {
        requestSchema: UpdateInterestAssignmentConditionApiArgSchema,
      },
    }),
    reorderInterestAssignmentConditions: build.mutation<
      void,
      ReorderInterestAssignmentConditionsApiArg
    >({
      query: (queryArg) => ({
        url: '/dms/v1/interest-resource/assignment-condition/reorder',
        method: 'POST',
        body: queryArg.reorderConditionsRequestBody,
      }),
      onQueryStarted(queryArg, {dispatch, queryFulfilled}) {
        const {conditions: conditionRanks} = queryArg.reorderConditionsRequestBody;

        // The list is always queried with {teamId: null} in the current UI.
        // If multi-team filtering is introduced, this cache key must be updated accordingly.
        const patchResult = dispatch(
          resourceAssignmentApi.util.updateQueryData(
            'listInterestAssignmentConditions',
            {teamId: null},
            (draft) => {
              const reorderedConditions = conditionRanks
                .sort((a, b) => a.rank - b.rank)
                .map(({conditionId}) => draft.find((c) => c.id === conditionId))
                .filter((c): c is (typeof draft)[number] => c !== undefined);

              return reorderedConditions.length === draft.length ? reorderedConditions : draft;
            }
          )
        );

        queryFulfilled.catch(() => {
          patchResult.undo();
        });
      },
      invalidatesTags: ['InterestAssignmentCondition'],
      extraOptions: {
        requestSchema: ReorderInterestAssignmentConditionsApiArgSchema,
      },
    }),
    assigneeTeamList: build.query<AssigneeTeamListApiResponse, AssigneeTeamListApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/${queryArg.resourceId}/${queryArg.recordId}/assignee/team`,
      }),
      extraOptions: {
        requestSchema: assigneeTeamListApiArgSchema,
      },
    }),
    ownerTeamList: build.query<OwnerTeamListApiResponse, OwnerTeamListApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/${queryArg.resourceId}/${queryArg.recordId}/owner/team`,
      }),
      extraOptions: {
        requestSchema: ownerTeamListApiArgSchema,
      },
    }),
    assignAssigneeFromTeam: build.mutation<void, AssignAssigneeFromTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/${queryArg.resourceId}/${queryArg.recordId}/assignee/team`,
        method: 'POST',
        body: queryArg.assignAssigneeFromTeamRequestBody,
      }),
      extraOptions: {
        requestSchema: AssignAssigneeFromTeamApiArgSchema,
      },
    }),
    assignOwnerFromTeam: build.mutation<void, AssignOwnerFromTeamApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/${queryArg.resourceId}/${queryArg.recordId}/owner/team`,
        method: 'POST',
        body: queryArg.assignOwnerFromTeamRequestBody,
      }),
      extraOptions: {
        requestSchema: AssignOwnerFromTeamApiArgSchema,
      },
    }),
  }),
});

export const {
  useListInterestAssignmentConditionsQuery,
  useAddInterestAssignmentConditionMutation,
  useRemoveInterestAssignmentConditionMutation,
  useUpdateInterestAssignmentConditionMutation,
  useReorderInterestAssignmentConditionsMutation,
  useAssigneeTeamListQuery,
  useOwnerTeamListQuery,
  useAssignAssigneeFromTeamMutation,
  useAssignOwnerFromTeamMutation,
} = resourceAssignmentApi;
